import React, { useMemo, useState, useEffect } from 'react'
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap'
import { FiX, FiCheck, FiShoppingBag, FiCheckSquare, FiSquare } from 'react-icons/fi'
import { useToasts } from 'react-toast-notifications'
import { isOrderRefunded, getOrderAmount } from '../../ManageOrder/helpers'
import { useCombinedPaymentOrders } from '../../ManageOrder/hooks'
import OrderItemRow from '../OrderItemRow'
import './style.scss'

const MAX_REFUND_ORDERS = 10

export default function ModalRefundOrder({
  data = {},
  modalRefundOrder,
  toggleRefundOrder,
  onConfirmRefund,
  products: eventProducts = [],
}) {
  const {
    combinedOrders,
    isLoadingCombined,
    totalCombinedAmount,
  } = useCombinedPaymentOrders(data, { enabled: modalRefundOrder })

  const ordersToDisplay = useMemo(() => {
    let list = []
    if (combinedOrders && combinedOrders.length > 0) {
      list = combinedOrders
    } else if (data?.listCombinedOrder && data.listCombinedOrder.length > 0) {
      list = data.listCombinedOrder
    } else if (data?.ref) {
      list = [data]
    }

    return [...list].sort((a, b) => {
      if (data?.ref) {
        if (a.ref === data.ref) return -1
        if (b.ref === data.ref) return 1
      }
      return (Number(a?.eOrderNo) || 0) - (Number(b?.eOrderNo) || 0)
    })
  }, [combinedOrders, data])

  const refundableOrders = useMemo(
    () => ordersToDisplay.filter((order) => !isOrderRefunded(order)),
    [ordersToDisplay]
  )

  const { addToast } = useToasts()
  const [selectedRefs, setSelectedRefs] = useState([])
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (modalRefundOrder) {
      if (data?.ref && !isOrderRefunded(data)) {
        setSelectedRefs([data.ref])
      } else {
        setSelectedRefs([])
      }
    } else {
      setSelectedRefs([])
    }
  }, [modalRefundOrder, data])

  const toggleSelectAll = () => {
    if (isSubmitting) return
    if (selectedRefs.length > 0) {
      setSelectedRefs([])
    } else {
      setSelectedRefs(refundableOrders.slice(0, MAX_REFUND_ORDERS).map((o) => o.ref))
    }
  }

  const toggleSelectOrder = (ref) => {
    if (isSubmitting) return
    const targetOrder = ordersToDisplay.find((o) => o.ref === ref)
    if (isOrderRefunded(targetOrder)) return

    if (selectedRefs.includes(ref)) {
      setSelectedRefs((prev) => prev.filter((r) => r !== ref))
    } else {
      if (selectedRefs.length >= MAX_REFUND_ORDERS) {
        addToast(`You can only select up to ${MAX_REFUND_ORDERS} orders at a time`, {
          appearance: 'warning',
          autoDismiss: true,
        })
        return
      }
      setSelectedRefs((prev) => [...prev, ref])
    }
  }

  const totalRefund = useMemo(() => {
    if (!ordersToDisplay || !ordersToDisplay.length) return 0
    return ordersToDisplay
      .filter((order) => selectedRefs.includes(order.ref))
      .reduce((sum, order) => sum + getOrderAmount(order), 0)
  }, [ordersToDisplay, selectedRefs])

  const totalPaid = useMemo(() => {
    if (data?.totalFee !== undefined && data?.totalFee !== null) {
      return Number(data.totalFee) || 0
    }
    if (totalCombinedAmount > 0) {
      return totalCombinedAmount
    }
    if (ordersToDisplay && ordersToDisplay.length > 0) {
      return ordersToDisplay.reduce((sum, order) => sum + getOrderAmount(order), 0)
    }
    return 0
  }, [data?.totalFee, totalCombinedAmount, ordersToDisplay])

  const handleConfirm = async () => {
    const selectedOrders = ordersToDisplay.filter((order) => selectedRefs.includes(order.ref))
    const refunds = selectedOrders.map((order) => ({
      ref: order.ref,
      amount: Number(getOrderAmount(order).toFixed(2)),
    }))

    if (onConfirmRefund) {
      try {
        setIsSubmitting(true)
        await onConfirmRefund(refunds, selectedOrders)
        toggleRefundOrder()
      } catch (err) {
        // Lỗi đã được xử lý bằng toast ở cấp gọi hàm
      } finally {
        setIsSubmitting(false)
      }
    } else {
      toggleRefundOrder()
    }
  }

  return (
    <Modal
      isOpen={modalRefundOrder}
      toggle={isSubmitting ? undefined : toggleRefundOrder}
      centered
      className="modal-refund-order-modern"
    >
      <ModalHeader className="refund-modern-header">
        <div className="header-top-row">
          <div className="title-group">
            <div className="refund-icon-badge">
              <FiShoppingBag />
            </div>
            <div>
              <h5 className="modal-title">Refund Orders</h5>
              <div className="customer-info">
                Customer: <span className="customer-name">{data?.uName || 'Guest'}</span>
              </div>
            </div>
          </div>
          <button
            type="button"
            className="btn-close-custom"
            onClick={toggleRefundOrder}
            disabled={isSubmitting}
            aria-label="Close"
          >
            <FiX />
          </button>
        </div>

        <div className="payment-overview-card">
          <div className="overview-item">
            <span className="overview-label">Original Payment</span>
            <span className="overview-val">${totalPaid.toFixed(2)}</span>
          </div>
          <div className="overview-divider" />
          <div className="overview-item">
            <span className="overview-label">Refund Amount</span>
            <span className="overview-val highlight">${totalRefund.toFixed(2)}</span>
          </div>
        </div>

        {refundableOrders.length > 1 && (
          <div className="selection-toolbar">
            <span className="selected-count">
              Selected <strong>{selectedRefs.length}</strong> of {refundableOrders.length} orders
              {refundableOrders.length > MAX_REFUND_ORDERS ? ` (Max ${MAX_REFUND_ORDERS})` : ''}
            </span>
            <button
              type="button"
              className="btn-select-all"
              onClick={toggleSelectAll}
              disabled={isSubmitting}
            >
              {selectedRefs.length > 0 ? (
                <>
                  <FiCheckSquare className="icon" /> Deselect All
                </>
              ) : (
                <>
                  <FiSquare className="icon" /> Select All{' '}
                  {refundableOrders.length > MAX_REFUND_ORDERS
                    ? `(First ${MAX_REFUND_ORDERS})`
                    : ''}
                </>
              )}
            </button>
          </div>
        )}
      </ModalHeader>

      <ModalBody className="refund-modern-body">
        {isLoadingCombined ? (
          <div className="refund-loading-state">
            <div className="spinner-border spinner-border-sm text-primary" role="status" />
            <span>Loading order breakdown...</span>
          </div>
        ) : (
          <div className="order-cards-list">
            {ordersToDisplay.map((order, idx) => {
              const isRefunded = isOrderRefunded(order)
              const isChecked = selectedRefs.includes(order.ref)
              const products = order?.allProductPerOrder || order?.items || []
              const orderTotal = getOrderAmount(order)

              return (
                <div
                  key={order.ref || idx}
                  className={`refund-order-card ${isChecked ? 'is-selected' : ''} ${
                    isRefunded ? 'is-disabled' : ''
                  }`}
                  onClick={() => !isRefunded && toggleSelectOrder(order.ref)}
                  role="button"
                  tabIndex={isRefunded ? -1 : 0}
                  aria-disabled={isRefunded}
                  onKeyDown={(e) => {
                    if (isRefunded) return
                    if (e.key === ' ' || e.key === 'Enter') {
                      e.preventDefault()
                      toggleSelectOrder(order.ref)
                    }
                  }}
                >
                  <div className="card-top">
                    <div className="order-id-group">
                      {isRefunded ? (
                        <span className="badge-refunded-tag">Refunded</span>
                      ) : (
                        <div className={`custom-checkbox ${isChecked ? 'checked' : ''}`}>
                          {isChecked && <FiCheck />}
                        </div>
                      )}
                      <span className="order-tag">Order #{order.eOrderNo}</span>
                      <span className="order-title-text" title={order.eTitle}>
                        {order.eTitle}
                      </span>
                    </div>
                    <div className="order-price-pill">${orderTotal.toFixed(2)}</div>
                  </div>

                  <div className="card-content">
                    <div className="products-list">
                      {products.map((item, pIdx) => (
                        <OrderItemRow
                          key={pIdx}
                          item={item}
                          isLast={pIdx === products.length - 1}
                          products={eventProducts}
                        />
                      ))}
                    </div>

                    <div className="fees-summary-chips">
                      <div className="fee-chip">
                        Admin: ${Number(order?.adminCostPerOrder || 0).toFixed(2)}
                      </div>
                      <div className="fee-chip">
                        Delivery: ${Number(order?.deliveryPerOrder || 0).toFixed(2)}
                      </div>
                      {Number(order?.discountPerOrder) > 0 && (
                        <div className="fee-chip discount">
                          Discount: -${Number(order?.discountPerOrder).toFixed(2)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </ModalBody>

      <ModalFooter className="refund-modern-footer">
        <div className="footer-actions">
          <button
            type="button"
            className="btn-cancel"
            onClick={toggleRefundOrder}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn-confirm"
            onClick={handleConfirm}
            disabled={
              isSubmitting ||
              isLoadingCombined ||
              selectedRefs.length === 0 ||
              refundableOrders.length === 0
            }
          >
            {isSubmitting ? (
              <>
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  style={{ marginRight: '6px' }}
                />
                Refunding...
              </>
            ) : (
              `Confirm Refund (${selectedRefs.length})`
            )}
          </button>
        </div>
      </ModalFooter>
    </Modal>
  )
}
